import { useMemo } from 'react'
import { TargetPlane } from '@scene'
import { reduce } from 'd3'
import { v4 as uuid } from 'uuid'
import { DEFAULT_SLAB_LIKE_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useGuidToElement } from '@editorHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { calculateBeamGeometry } from 'src/utils'
import RectangularShapeDrawer2D from '../RectangularShapeDrawer2D'
import { NUM_DECIMALS_ROUND_VERTICAL_SLAB_DRAWER_2D } from './constants'

interface Props {
  storey: string
}

const VerticalSlabDrawer2D = ({ storey }: Props) => {
  const model = useModelStore(state => state.model)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const drawSlabOnElement = useEditElementStore(state => state.drawSlabOnElement)
  const addSlab = useModelStore(state => state.addSlab)
  const elements = useGuidToElement()

  // artificial id used for storey
  const storeyId = useMemo(() => uuid(), [storey])

  const storeyPoints = useMemo(() => {
    const upperBoundary = model.storey_boundaries[storey][1]
    return [
      new ImmutableVector3(-100, -100, upperBoundary),
      new ImmutableVector3(-100, 100, upperBoundary),
      new ImmutableVector3(100, 100, upperBoundary),
      new ImmutableVector3(100, -100, upperBoundary),
    ]
  }, [model.storey_boundaries, storey])

  const storeyTarget = useMemo(() => {
    return {
      [storeyId]: storeyPoints,
    }
  }, [storeyId, storeyPoints])

  const targetElements = useMemo(
    () =>
      reduce(
        [...model.roof_slabs, ...model.slabs],
        (collector, element) => ({ ...collector, [element.guid]: element.shape.points }),
        {} as Record<string, ImmutableVector3[]>,
      ),
    [model],
  )

  useDefault2DSnapConfigForElementType(SnapTargetElementType.VERTICAL_SLABS)

  return (
    <>
      <RectangularShapeDrawer2D
        targetElements={drawSlabOnElement ? targetElements : storeyTarget}
        roundNumDecimalsXY={NUM_DECIMALS_ROUND_VERTICAL_SLAB_DRAWER_2D}
        onRectangleDrawn={(points, guid) => {
          const slabGuid = uuid()
          const beamGuid = uuid()
          const relativePosition = 0.5

          // Calculate beam geometry using utility function
          const { start: startPoint, end: endPoint } = calculateBeamGeometry(
            points,
            relativePosition,
          )
          const slab: VerticalSlab = {
            guid: slabGuid,
            shape: { points: points },
            storey: drawSlabOnElement ? elements[guid].storey : storey,
            element_type: 'vertical_slabs' as ElementTypes,
            is_local: true,
            element_guid: beamGuid,
            ...DEFAULT_SLAB_LIKE_SETTINGS,
            beam: {
              guid: beamGuid,
              element_guid: beamGuid,
              slab_guid: slabGuid,
              relative_position: relativePosition,
              element_type: 'slab_beams' as ElementTypes,
              shape: {
                start: startPoint,
                end: endPoint,
              },
              storey: drawSlabOnElement ? elements[guid].storey : storey,
              is_local: true,
            } as SlabBeam,
            step_size: 0,
          }

          setActiveElement(slab.guid)
          addSlab(slab)
        }}
      />
      {!drawSlabOnElement && <TargetPlane name={storeyId} points={storeyPoints} />}
    </>
  )
}

export default VerticalSlabDrawer2D
